import React, { useContext, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontScaleContext } from "../App";
import CurrencyBreakdown from "./CurrencyBreakdown";

export default function ValuesBox({
    name = "Executive Sofa Chair",
    description = "Relaxing leather comfort",
    originalPrice = "3c",
    minHabloon = "1000",
    maxHabloon = "1500",
    avgHabloon = "1250",
    largeImg = null,
    smallImg = null,
    iconImg = null,
    lastUpdated = null,
    itemId = null,
    onAddToCart = null,
    cartQty = 0,
    onUpdateCartQty = null,
}) {
    const placeholderLarge = `${process.env.PUBLIC_URL}/img/Placeholder.png`;
    const placeholderSmall = `${process.env.PUBLIC_URL}/img/Placeholder_s.png`;
    const placeholderIcon = `${process.env.PUBLIC_URL}/img/Placeholder_i.png`;

    const displayLargeImg = largeImg || placeholderLarge;
    const displaySmallImg = smallImg || placeholderSmall;
    const displayIconImg = iconImg || placeholderIcon;

    const fontScale = useContext(FontScaleContext);

    const [largeMissing, setLargeMissing] = useState(false);
    const [smallMissing, setSmallMissing] = useState(false);
    const [iconMissing, setIconMissing] = useState(false);

    const [largeOffset, setLargeOffset] = useState({ x: 0, y: 0 });
    const [smallOffset, setSmallOffset] = useState({ x: 0, y: 0 });

    const [localQty, setLocalQty] = useState(1);
    const [showBreakdown, setShowBreakdown] = useState(false);

    const inCart = cartQty > 0;
    const qty = inCart ? cartQty : localQty;

    const largeDragRef = useRef({
        dragging: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        baseX: 0,
        baseY: 0,
    });

    const smallDragRef = useRef({
        dragging: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        baseX: 0,
        baseY: 0,
    });

    const largeHasOffset = useMemo(
        () => largeOffset.x !== 0 || largeOffset.y !== 0,
        [largeOffset]
    );
    const smallHasOffset = useMemo(
        () => smallOffset.x !== 0 || smallOffset.y !== 0,
        [smallOffset]
    );

    const rawMin = Math.floor(Number(minHabloon) || 0);
    const rawAvg = Math.floor(Number(avgHabloon) || 0);
    const rawMax = Math.floor(Number(maxHabloon) || 0);

    const displayMin = rawMin * qty;
    const displayAvg = rawAvg * qty;
    const displayMax = rawMax * qty;

    const formatRelativeTime = (dateString) => {
        if (!dateString) return "N/A";

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffYears > 0) return `${diffYears} ${diffYears === 1 ? "year" : "years"} ago`;
        if (diffMonths > 0) return `${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
        if (diffWeeks > 0) return `${diffWeeks} ${diffWeeks === 1 ? "week" : "weeks"} ago`;
        if (diffDays > 0) return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
        if (diffHours > 0) return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        if (diffMins > 0) return `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
        return "Just now";
    };

    const startDrag = (e, kind) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const ref = kind === "large" ? largeDragRef : smallDragRef;
        const current = kind === "large" ? largeOffset : smallOffset;

        ref.current.dragging = true;
        ref.current.pointerId = e.pointerId;
        ref.current.startX = e.clientX;
        ref.current.startY = e.clientY;
        ref.current.baseX = current.x;
        ref.current.baseY = current.y;

        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const moveDrag = (e, kind) => {
        const ref = kind === "large" ? largeDragRef : smallDragRef;
        const setOffset = kind === "large" ? setLargeOffset : setSmallOffset;

        if (!ref.current.dragging) return;
        if (ref.current.pointerId !== e.pointerId) return;

        e.preventDefault();
        e.stopPropagation();

        const dx = e.clientX - ref.current.startX;
        const dy = e.clientY - ref.current.startY;

        setOffset({
            x: Math.round(ref.current.baseX + dx),
            y: Math.round(ref.current.baseY + dy),
        });
    };

    const endDrag = (e, kind) => {
        const ref = kind === "large" ? largeDragRef : smallDragRef;
        if (!ref.current.dragging) return;
        if (ref.current.pointerId !== e.pointerId) return;

        e.preventDefault();
        e.stopPropagation();

        ref.current.dragging = false;
        ref.current.pointerId = null;

        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch (_) {}
    };

    const ResetButton = ({ onClick }) => (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
            }}
            onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            className="absolute top-1 left-1 w-6 h-6 rounded bg-black/60 hover:bg-black/70 active:bg-black/80 flex items-center justify-center z-10"
            style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.25)" }}
            aria-label="Reset location"
            title="Reset location"
        >
            <span className="text-white text-[14px] leading-none select-none">↺</span>
        </button>
    );

    const PannablePreview = ({
        kind,
        missing,
        setMissing,
        src,
        alt,
        w,
        h,
        offset,
        setOffset,
        hasOffset,
        dragRef,
    }) => (
        <div
            className={[
                "relative rounded overflow-hidden border border-black/10 shrink-0",
                missing ? "bg-gray-200/70 opacity-40 grayscale" : "bg-white/60",
            ].join(" ")}
            style={{ width: w, height: h }}
        >
            {!missing && hasOffset && <ResetButton onClick={() => setOffset({ x: 0, y: 0 })} />}

            {missing ? (
                <div className="w-full h-full flex items-center justify-center">
                    <div className="text-[10px] font-bold tracking-wide text-gray-600 uppercase select-none">
                        No Image
                    </div>
                </div>
            ) : (
                <div
                    className="w-full h-full"
                    style={{ touchAction: "none" }}
                    onClickCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onPointerDown={(e) => startDrag(e, kind)}
                    onPointerMove={(e) => moveDrag(e, kind)}
                    onPointerUp={(e) => endDrag(e, kind)}
                    onPointerCancel={(e) => endDrag(e, kind)}
                >
                    <img
                        src={src}
                        onError={() => setMissing(true)}
                        alt={alt}
                        draggable={false}
                        className="block absolute"
                        style={{
                            left: "50%",
                            top: "50%",
                            imageRendering: "pixelated",
                            width: "auto",
                            height: "auto",
                            maxWidth: "none",
                            maxHeight: "none",
                            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                            cursor: dragRef.current.dragging ? "grabbing" : "grab",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                        }}
                        loading="eager"
                    />
                </div>
            )}
        </div>
    );

    const renderNormalValueCell = (label, displayValue, bgClass) => (
        <div
            className={`overflow-hidden h-[50px] rounded px-2 border border-black/5 flex flex-row items-center ${bgClass}`}
        >
            <div className="mr-2 w-[25px] h-[25px] shrink-0 flex items-center justify-center">
                <img
                    src="/img/habloon_fullsize.png"
                    alt=""
                    draggable={false}
                    style={{
                        width: "auto",
                        height: "auto",
                        maxWidth: "none",
                        maxHeight: "none",
                        imageRendering: "pixelated",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                    }}
                />
            </div>
            <div className="flex flex-col">
                <div className="text-[10px] tracking-wide font-bold text-gray-600 leading-none">
                    {label}
                </div>
                <div className="text-sm font-bold text-gray-900 leading-none mt-1 tabular-nums">
                    {displayValue}
                </div>
            </div>
        </div>
    );

    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onAddToCart) {
            onAddToCart({
                id: itemId,
                name,
                iconImg: displayIconImg,
                minHabloon: rawMin,
                avgHabloon: rawAvg,
                maxHabloon: rawMax,
                qty,
            });
            toast.success(`${name} ×${qty} added to cart`, { autoClose: 2000 });
        }
    };

    const adjustQty = (e, delta) => {
        e.preventDefault();
        e.stopPropagation();
        if (inCart && onUpdateCartQty) {
            onUpdateCartQty(itemId, cartQty + delta); // caller removes at <= 0
        } else {
            setLocalQty((q) => Math.max(1, q + delta));
        }
    };

    return (
        <div
            className="bg-gray-200 p-3 w-full max-w-full overflow-hidden min-h-[270px] flex flex-row flex-wrap items-stretch gap-3 min-w-0"
            style={{ boxShadow: "0 3px 0 0 rgba(0, 0, 0, 0.07)" }}
        >
            <div className="w-[120px] flex flex-col items-center gap-3 shrink-0">
                <PannablePreview
                    kind="large"
                    missing={largeMissing}
                    setMissing={setLargeMissing}
                    src={displayLargeImg}
                    alt={name}
                    w={120}
                    h={120}
                    offset={largeOffset}
                    setOffset={setLargeOffset}
                    hasOffset={largeHasOffset}
                    dragRef={largeDragRef}
                />

                <PannablePreview
                    kind="small"
                    missing={smallMissing}
                    setMissing={setSmallMissing}
                    src={displaySmallImg}
                    alt={`${name} small`}
                    w={120}
                    h={80}
                    offset={smallOffset}
                    setOffset={setSmallOffset}
                    hasOffset={smallHasOffset}
                    dragRef={smallDragRef}
                />
            </div>

            <div className="min-w-0 flex-1 flex flex-col">
                <div className="min-w-0 flex flex-row items-start gap-2">
                    <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{name}</div>
                        <div
                            className={`text-xs text-gray-600 mt-1 ${
                                fontScale > 1.1 ? "line-clamp-1" : "line-clamp-2"
                            }`}
                        >
                            {description}
                        </div>
                    </div>

                    <div className="ml-auto w-[40px] h-[40px] rounded overflow-hidden bg-white/60 border border-black/10 flex items-center justify-center shrink-0">
                        {iconMissing ? null : (
                            <img
                                src={displayIconImg}
                                onError={() => setIconMissing(true)}
                                alt={`${name} icon`}
                                className="block"
                                style={{
                                    imageRendering: "pixelated",
                                    width: "auto",
                                    height: "auto",
                                    maxWidth: "none",
                                    maxHeight: "none",
                                    userSelect: "none",
                                    WebkitUserSelect: "none",
                                }}
                                loading="eager"
                                draggable={false}
                            />
                        )}
                    </div>
                </div>

                <div className="mt-auto space-y-2">
                    <div className="overflow-hidden h-[40px] bg-white/60 rounded px-2 border border-black/5 w-full flex flex-row mt-2 items-center">
                        <div className="text-[10px] tracking-wide font-bold text-gray-600 leading-none">
                            Original Price (Credits)
                        </div>

                        <div className="ml-auto text-sm flex flex-row font-bold text-gray-900 leading-none">
                            {originalPrice} <img src="/img/values.gif" className="ml-1" alt="credits" />
                        </div>
                    </div>

                    {/* Value cells — hover/tap to show currency breakdown for all three */}
                    <div
                        style={fontScale <= 1.2 ? { zoom: 1 / fontScale } : undefined}
                        className="cursor-pointer select-none"
                        onMouseEnter={() => setShowBreakdown(true)}
                        onMouseLeave={() => setShowBreakdown(false)}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBreakdown((v) => !v);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        {showBreakdown ? (
                            <div className="bg-white/60 rounded border border-black/10 p-2">
                                <div className={`grid gap-2 ${fontScale > 1.2 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3"}`}>
                                    {[
                                        { label: "Minimum", value: displayMin },
                                        { label: "Average", value: displayAvg },
                                        { label: "Maximum", value: displayMax },
                                    ].map(({ label, value }) => (
                                        <div key={label} className="flex flex-col gap-1">
                                            <div className="text-[10px] tracking-wide font-bold text-gray-600 leading-none">
                                                {label}
                                            </div>
                                            <CurrencyBreakdown value={value} size="sm" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className={`grid gap-2 ${fontScale > 1.2 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-3"}`}>
                                {renderNormalValueCell("Minimum", displayMin, "bg-white/60")}
                                {renderNormalValueCell("Average", displayAvg, "bg-gold")}
                                {renderNormalValueCell("Maximum", displayMax, "bg-white/60")}
                            </div>
                        )}
                    </div>

                    {/* Footer: qty + add to cart */}
                    <div className="pt-2 border-t border-black/10 flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5">
                        {/* Quantity control — tinted when in cart */}
                        <div
                            className={`flex items-center gap-1 rounded-full px-2 py-1 border shrink-0 transition-colors ${
                                inCart ? "bg-maroon/10 border-maroon/30" : "bg-white/60 border-black/5"
                            }`}
                            style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.06)" }}
                        >
                            {inCart && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-maroon shrink-0">
                                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                            )}
                            <span className={`text-[10px] font-bold leading-none shrink-0 ${inCart ? "text-maroon" : "text-gray-500"}`}>
                                {inCart ? "In Cart" : "Quantity"}
                            </span>
                            <button
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => adjustQty(e, -1)}
                                className="w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 active:bg-gray-400 flex items-center justify-center text-gray-700 font-bold text-sm leading-none transition-colors shrink-0"
                                title={inCart && qty === 1 ? "Remove from cart" : undefined}
                            >
                                −
                            </button>
                            <span className="text-[12px] font-bold text-gray-900 w-5 text-center tabular-nums leading-none">
                                {qty}
                            </span>
                            <button
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => adjustQty(e, 1)}
                                className="w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 active:bg-gray-400 flex items-center justify-center text-gray-700 font-bold text-sm leading-none transition-colors shrink-0"
                            >
                                +
                            </button>
                        </div>

                        {/* Add to Cart — hidden once already in cart */}
                        {onAddToCart && !inCart && (
                            <button
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={handleAddToCart}
                                className="flex items-center gap-1.5 px-2.5 py-1 bg-maroon hover:bg-maroon/90 active:bg-maroon/80 text-white rounded-full text-[11px] font-bold tracking-wide shrink-0 transition-colors"
                                style={{ boxShadow: "0 2px 0 rgba(0,0,0,0.18)" }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                                Add to Cart
                            </button>
                        )}
                    </div>
                    <div className="pt-2 border-t border-black/10 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                        {/* Last Updated */}
                        <div className="ml-auto flex items-center gap-1">
                            <span className="text-[10px] tracking-wide font-bold text-gray-500 leading-none">
                                Last Updated
                            </span>
                            <span className="text-[10px] font-bold text-gray-500 leading-none">
                                {formatRelativeTime(lastUpdated)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}